import { useState, useCallback } from 'react';
import { transactions as transactionsApi } from '@/lib/api';
import type {
  Transaction,
  TransactionFilters,
  CreateTransactionData,
  UpdateTransactionData,
} from '@/lib/types';

interface UseTransactionsReturn {
  transactions: Transaction[];
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
  total: number;
  fetchTransactions: (filters?: TransactionFilters) => Promise<void>;
  createTransaction: (data: CreateTransactionData) => Promise<Transaction>;
  updateTransaction: (
    id: string,
    data: UpdateTransactionData
  ) => Promise<Transaction>;
  deleteTransaction: (id: string) => Promise<void>;
}

export function useTransactions(): UseTransactionsReturn {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [currentFilters, setCurrentFilters] = useState<TransactionFilters>({});

  const fetchTransactions = useCallback(
    async (filters: TransactionFilters = {}) => {
      setLoading(true);
      setError(null);
      setCurrentFilters(filters);

      try {
        const response = await transactionsApi.list(filters);
        setTransactions(response.data);
        setPage(response.page);
        setTotalPages(response.totalPages);
        setTotal(response.total);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to fetch transactions';
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const createTransaction = useCallback(
    async (data: CreateTransactionData): Promise<Transaction> => {
      setError(null);
      try {
        const transaction = await transactionsApi.create(data);
        await fetchTransactions(currentFilters);
        return transaction;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to create transaction';
        setError(message);
        throw err;
      }
    },
    [fetchTransactions, currentFilters]
  );

  const updateTransaction = useCallback(
    async (
      id: string,
      data: UpdateTransactionData
    ): Promise<Transaction> => {
      setError(null);
      try {
        const transaction = await transactionsApi.update(id, data);
        await fetchTransactions(currentFilters);
        return transaction;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to update transaction';
        setError(message);
        throw err;
      }
    },
    [fetchTransactions, currentFilters]
  );

  const deleteTransaction = useCallback(
    async (id: string): Promise<void> => {
      setError(null);
      try {
        await transactionsApi.delete(id);
        await fetchTransactions(currentFilters);
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to delete transaction';
        setError(message);
        throw err;
      }
    },
    [fetchTransactions, currentFilters]
  );

  return {
    transactions,
    loading,
    error,
    page,
    totalPages,
    total,
    fetchTransactions,
    createTransaction,
    updateTransaction,
    deleteTransaction,
  };
}
